import { type Collection, Map as ImmutableMap, List, OrderedMap } from 'immutable';
import { DateTime } from 'luxon';
import { shallowEqual } from 'react-redux';
import { createSelector as createSelectorBase } from 'reselect';

import * as institutionLoginsSelectors from 'app-components/flux/institution-logins/institutionLoginsSelectors';
import type {
  AggStatus,
  INSTITUTION_LOGINS_REDUCER_KEY,
} from 'app-components/flux/institution-logins/institutionLoginsTypes';
import * as institutionLoginsUtils from 'app-components/flux/institution-logins/institutionLoginsUtils';
import { registerCacheReset } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors, accountsUtils } from '../accounts';
import type { Account, AccountSubType, AccountType, NonGoalAccount } from '../accounts/accountsTypes';
import { type BalanceLabelMode, classifyAccountType } from '../accounts/accountsUtils';
import { type BUSINESS_REDUCER_KEY, businessSelectors } from '../business';
import { balanceSelectors } from '../calculated-balances';
import { ShowBalance } from '../calculated-balances/balanceTypes';
import type { DATE_REDUCER_KEY } from '../date/dateTypes';
import { type ENTITLEMENTS_REDUCER_KEY, entitlementsSelectors } from '../entitlements';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { investmentHoldingsV2Selectors } from '../investment-holdings-v2';
import { type PREFERENCES_V2_REDUCER_KEY, preferencesV2Selectors } from '../preferences-v2';
import type { SECURITIES_REDUCER_KEY } from '../securities/securitiesTypes';
import { AccountNode, type AccountNodeDepth, type AccountNodeProps } from './accountNodeTypes';
import {
  type GroupingData,
  getL1GroupingData,
  getL2GroupingData,
  hasL2Grouping,
  type L2GroupingId,
  l2Substrings,
} from './accountNodeUtils';

type State = Pick<
  RootState,
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof BUSINESS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
  | typeof ENTITLEMENTS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof INSTITUTION_LOGINS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof PREFERENCES_V2_REDUCER_KEY
>;
const createSelector = createSelectorBase.withTypes<State>();

type AccountNodesById = ImmutableMap<string, Account>;
type AccountCollection = Collection<unknown, Account>;
type AccountBarOrder = Record<string, number> | undefined;
type BalanceMap = ReturnType<typeof balanceSelectors.getBalancesByAccountIds>;
type InvestmentTotalsByAccountId = ImmutableMap<string, number>;

type MutableAccountNode = Partial<Omit<AccountNodeProps, 'account' | 'allAccountIds' | 'children'>> & {
  id: string;
  depth: AccountNodeDepth;
  displayLabel: string;
  displayOrder: number;
  account?: Account | null;
  allAccountIds?: List<string> | null;
  children?: MutableAccountNode[] | null;
};

export type AccountNodesTreeParams = {
  ignoreUsageType?: boolean;
  includeClosedOverride?: boolean;
  ignoreHiddenGrouping?: boolean;
};

export type TransactionsTabVisibility = {
  showSpendingTab: boolean;
  showIncomeTab: boolean;
};

export type TransactionsStatusBannerState = {
  showAppleWalletBanner: boolean;
  showInstitutionStatusBanner: boolean;
  institutionLoginId: string | null;
  account?: Account;
};

export type AccountReorderNode = {
  businessId: string;
  displayThemeColor: string;
  displayThemeBgColor: string;
  displayLabel: string;
};

export type BalanceLabelModeOptions = {
  includePendingOverride?: boolean;
  manualRefreshEnabledOverride?: boolean;
  isAppleTotalBalanceOverride?: boolean;
};

export type StatusBannerVisibilityForAccountId = {
  appleWalletVisible: boolean;
  accountVisible: boolean | undefined;
};

const blockedAccountTypes = new Set<AccountType>(['ACCOUNTS_RECEIVABLE', 'ACCOUNTS_PAYABLE', 'PAYMENT']);
const blockedAccountSubTypes = new Set<AccountSubType>(['SALES_TAX', 'UNDEPOSITED_FUNDS']);
const assetAccountTypes = new Set<AccountType>(['REAL_ESTATE', 'VEHICLE', 'OTHER_ASSET']);
const APPLE_WALLET_CHANNEL_TYPE = 'APPLE_WALLET';

const isManualAccountRecord = (account?: Account | null): boolean => accountsUtils.isAccountManual(account?.toObject());

function balRound(val: number | null | undefined): number {
  const ret = val ? Number(val.toFixed(2)) : 0;
  return ret || 0;
}

function getAccountName(account: Account): string {
  assert(account.name, 'name not defined');
  return account.name ?? '';
}

function hasHiddenChild(node: MutableAccountNode, ignoreHiddenGrouping = false): boolean {
  return Boolean(node.children?.find((child) => child.account?.isExcludedFromAccountBar && !ignoreHiddenGrouping));
}

const accountIdsCache: Record<string, List<string>> = {};
function memoizedAccountIdList(ids: string[]): List<string> {
  const normalizedIds = [...ids].sort((a, b) => a.localeCompare(b));
  const key = JSON.stringify(normalizedIds);
  if (!accountIdsCache[key]) {
    accountIdsCache[key] = List(normalizedIds);
  }
  return accountIdsCache[key];
}

const findOrCreateNodeForGroup = (
  list: MutableAccountNode[],
  data: GroupingData & Partial<MutableAccountNode>,
  depth: AccountNodeDepth,
): MutableAccountNode => {
  let foundNode = list.find((node) => node.id === data.id);
  if (!foundNode) {
    foundNode = {
      ...data,
      depth,
      children: [],
    };
    list.push(foundNode);
  }
  return foundNode;
};

const getAllNodeForAccounts = (accountsById: AccountNodesById, allAccountsLabel: boolean): MutableAccountNode => {
  const allNodes: MutableAccountNode = {
    id: 'all',
    displayLabel: allAccountsLabel ? 'All Accounts' : 'Net Worth',
    routeTo: '/transactions',
    displayOrder: 0,
    expandable: false,
    depth: 1,
    children: [],
  };
  accountsById.forEach((account) => {
    if (account.type !== 'GOAL') {
      allNodes.children?.push({
        id: account.id || account.clientId || '',
        depth: 2,
        displayLabel: getAccountName(account),
        displayOrder: 0,
        routeTo: '/transactions',
        account,
      });
    }
  });

  allNodes.children?.sort((a, b) => {
    let compareVal =
      getL1GroupingData(a.account as Account).displayOrder - getL1GroupingData(b.account as Account).displayOrder;
    if (compareVal === 0) {
      compareVal =
        getL2GroupingData(a, a.account as Account).displayOrder -
        getL2GroupingData(b, b.account as Account).displayOrder;
    }
    return compareVal;
  });

  return allNodes;
};

const getAccountNodesTreeForAccounts = (
  accounts: AccountCollection | Account[],
  ignoreHiddenGrouping = false,
): MutableAccountNode[] => {
  const accountList = Array.isArray(accounts) ? accounts : accounts.valueSeq().toArray();

  const l1Nodes = accountList.reduce<MutableAccountNode[]>((list, account) => {
    const l1Group = findOrCreateNodeForGroup(list, getL1GroupingData(account), 1);
    l1Group.children?.push({
      id: account.id || account.clientId || '',
      depth: 2,
      displayLabel: getAccountName(account),
      displayOrder: 0,
      account,
      balanceType: l1Group.balanceType,
    });
    return list;
  }, []);

  l1Nodes.sort((a, b) => a.displayOrder - b.displayOrder);

  l1Nodes.forEach((node) => {
    const children = node.children ?? [];
    if (hasL2Grouping(node.id, children.length) || hasHiddenChild(node, ignoreHiddenGrouping)) {
      node.children = children.reduce<MutableAccountNode[]>((list, child) => {
        if (!child.account) return list;
        const l2Group = findOrCreateNodeForGroup(list, getL2GroupingData(node, child.account, ignoreHiddenGrouping), 2);
        l2Group.children?.push({
          id: child.account.id || child.account.clientId || '',
          depth: 3,
          displayLabel: getAccountName(child.account),
          displayOrder: 0,
          routeTo: l2Group.routeTo,
          account: child.account,
          balanceType: l2Group.balanceType,
        });

        return list;
      }, []);
      node.children.sort((a, b) => a.displayOrder - b.displayOrder);
      node.children.forEach((l3Node) => {
        l3Node.children?.sort((a, b) =>
          (a.displayLabel ?? '').localeCompare(b.displayLabel ?? '', undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        );
      });
    } else {
      children.sort((a, b) =>
        (a.displayLabel ?? '').localeCompare(b.displayLabel ?? '', undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );
      children.forEach((l3Node) => {
        l3Node.routeTo = node.routeTo;
        l3Node.isLeaf = true;
      });
    }
  });

  return l1Nodes;
};

const clearRoutesForInvestmentNode = (l1Node: MutableAccountNode): MutableAccountNode => {
  if (l1Node.id !== 'INVESTMENT') return l1Node;

  l1Node.routeTo = null;
  l1Node.children = l1Node.children?.map((l2Node) => {
    l2Node.routeTo = null;
    l2Node.children = l2Node.children?.map((l3Node) => {
      l3Node.routeTo = null;
      return l3Node;
    });
    return l2Node;
  });
  return l1Node;
};

const rollupAccountNodes = ({
  node,
  balancesById,
  hideGoals,
  ignoreHiddenGrouping,
  investTotals,
  securitiesFF,
}: {
  node: MutableAccountNode;
  balancesById: BalanceMap;
  hideGoals: boolean;
  ignoreHiddenGrouping: boolean;
  investTotals: InvestmentTotalsByAccountId;
  securitiesFF: boolean;
}): AccountNode => {
  let allAccountIds: string[] = [];
  let balance = 0;
  let goalsBalance = 0;
  let currency: string | null = null;

  if (node.id !== 'GOALS' && node.children) {
    const children = node.children.map((child) => {
      const childAfterRollup = rollupAccountNodes({
        node: child,
        balancesById,
        hideGoals,
        ignoreHiddenGrouping,
        investTotals,
        securitiesFF,
      });
      allAccountIds = allAccountIds.concat(childAfterRollup.allAccountIds?.toArray() ?? []);
      balance += childAfterRollup.balance ?? 0;
      if (!hideGoals) {
        goalsBalance += childAfterRollup.goalsBalance ?? 0;
      }

      if (!currency) {
        currency = childAfterRollup.currency ?? null;
      } else if (currency !== childAfterRollup.currency) {
        currency = 'N/A';
      }

      return childAfterRollup;
    });
    node.children = null;
    const nodeWithChildren = {
      ...node,
      children: List(children),
    };
    return AccountNode({
      ...nodeWithChildren,
      allAccountIds: memoizedAccountIdList(allAccountIds),
      balance: balRound(balance),
      goalsBalance: hideGoals ? 0 : balRound(goalsBalance),
      availableBalance: balRound(balance) - (hideGoals ? 0 : balRound(goalsBalance)),
      currency,
      hide: node.account ? Boolean(node.account.isExcludedFromAccountBar && !ignoreHiddenGrouping) : false,
    });
  }

  if (node.account?.id) {
    allAccountIds.push(node.account.id);

    const balancesForAccount = balancesById.get(node.account.id);
    if (balancesForAccount) {
      balance += balancesForAccount.balance;
      if (!hideGoals) {
        goalsBalance += balancesForAccount.goalsBalance;
      }
    }

    if (!currency) {
      currency = node.account.currency ?? null;
    } else if (currency !== node.account.currency) {
      currency = 'N/A';
    }
  }

  if (isManualAccountRecord(node.account) && node.account?.type === 'INVESTMENT' && securitiesFF) {
    balance = node.account.id ? (investTotals.get(node.account.id) ?? 0) : 0;
  } else {
    balance = balRound(balance);
  }
  goalsBalance = hideGoals ? 0 : balRound(goalsBalance);

  return AccountNode({
    ...node,
    children: null,
    allAccountIds: memoizedAccountIdList(allAccountIds),
    balance,
    goalsBalance,
    availableBalance: balance - goalsBalance,
    currency,
    hide: node.account ? Boolean(node.account.isExcludedFromAccountBar && !ignoreHiddenGrouping) : false,
  });
};

const accountNodeKeys = AccountNode().toSeq().keySeq().toArray();

// Hands back the previous node when every field is identical, so unchanged rows keep their identity.
function reuseAccountNode(previous: AccountNode | undefined, next: AccountNode): AccountNode {
  if (!previous) return next;
  const children =
    previous.children && next.children ? reuseAccountNodes(previous.children, next.children) : next.children;
  const isUnchanged =
    children === previous.children &&
    accountNodeKeys.every((key) => key === 'children' || Object.is(previous.get(key), next.get(key)));
  if (isUnchanged) return previous;
  return children === next.children ? next : next.set('children', children);
}

// Matches siblings by id and keeps the previous list when every node in it was reused in place.
function reuseAccountNodes(previous: List<AccountNode>, next: List<AccountNode>): List<AccountNode> {
  const previousById = new Map<string | null, AccountNode>();
  for (const node of previous) {
    previousById.set(node.id, node);
  }
  let isUnchanged = previous.size === next.size;
  const nodes = next.map((node, index) => {
    const reused = reuseAccountNode(previousById.get(node.id), node);
    isUnchanged = isUnchanged && reused === previous.get(index);
    return reused;
  });
  return isUnchanged ? previous : nodes;
}

// The last tree per params; the result function is shared by every params combination.
const previousTreesByParams = new Map<string, List<AccountNode>>();
registerCacheReset(() => previousTreesByParams.clear());

const emptyInvestmentTotals: InvestmentTotalsByAccountId = ImmutableMap<string, number>();
const getAccountTreeInvestmentTotals = (state: State): InvestmentTotalsByAccountId =>
  featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities')
    ? investmentHoldingsV2Selectors.getInvestmentHoldingsTotalsByAccountId(state)
    : emptyInvestmentTotals;

export const getAccountNodesTree = createSelector(
  [
    accountsSelectors.getAccountsById,
    balanceSelectors.getBalancesByAccountIds,
    businessSelectors.getResourcesById,
    (state) => Boolean(preferencesV2Selectors.getPreferenceValue(state, 'hideClosedAccBar')),
    (state) => Boolean(preferencesV2Selectors.getPreferenceValue(state, 'hideGoalsAccBar')),
    (state): AccountBarOrder => {
      if (entitlementsSelectors.getBusinessEnabled(state)) {
        return preferencesV2Selectors.getPreferenceValue(state, 'sAccountBarOrder');
      }
      return undefined;
    },
    getAccountTreeInvestmentTotals,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
    (_state, params?: AccountNodesTreeParams) => params?.ignoreUsageType ?? false,
    (state) => businessSelectors.getDefaultBusiness(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'allAccountsLabelChange'),
    (_state, params?: AccountNodesTreeParams) => params?.includeClosedOverride ?? false,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentsAcme'),
    (_state, params?: AccountNodesTreeParams) => params?.ignoreHiddenGrouping ?? false,
  ],
  (
    accountsById,
    balancesById,
    businessesById,
    hideClosedPref,
    hideGoals,
    accountBarOrder,
    investTotals,
    securitiesFF,
    ignoreUsageType,
    defaultBusiness,
    allAccountsLabel,
    includeClosedOverride,
    investmentsAcme,
    ignoreHiddenGrouping,
  ): List<AccountNode> => {
    const rootNodes: MutableAccountNode[] = [];
    const hideClosed = Boolean(hideClosedPref && !includeClosedOverride);

    const allNodes = getAllNodeForAccounts(accountsById, Boolean(allAccountsLabel));
    rootNodes.push(allNodes);

    const filteredAccounts = accountsById.filter((account) => !hideClosed || !account.isClosed);
    const accountByUsages = filteredAccounts.reduce<Map<string, Account[]>>((map, account) => {
      const usage = account.businessId || 'PERSONAL';
      if (!map.has(usage)) {
        map.set(usage, []);
      }
      map.get(usage)?.push(account);
      return map;
    }, new Map());

    if (accountByUsages.size === 1 || ignoreUsageType) {
      const l1Nodes = getAccountNodesTreeForAccounts(filteredAccounts, ignoreHiddenGrouping).map((l1Node) => {
        if (!investmentsAcme) {
          return clearRoutesForInvestmentNode(l1Node);
        }
        return l1Node;
      });
      rootNodes.push(...l1Nodes);
    } else {
      accountByUsages.forEach((accounts, usage) => {
        let createdAt: string | undefined;
        let displayLabel = 'Personal';
        let displayThemeBgColor = 'background.layer1';
        let displayThemeColor: string | undefined;

        if (usage !== 'PERSONAL') {
          const business = businessesById.get(usage);
          createdAt = business?.createdAt || '';
          displayLabel = business?.name || '';
          displayThemeBgColor = business?.color ? `${business.color}.light` : 'background.layer1';
          displayThemeColor = business?.color ? `${business.color}.dark` : 'text.primary';
        }
        const l0Node = findOrCreateNodeForGroup(
          rootNodes,
          {
            id: usage,
            createdAt,
            displayLabel,
            displayThemeBgColor,
            displayThemeColor,
            displayOrder: 0,
            expandable: true,
            routeTo: '/transactions',
          },
          1,
        );
        l0Node.children = getAccountNodesTreeForAccounts(accounts, ignoreHiddenGrouping);
      });
      rootNodes.sort((a, b) => {
        if (a.id === 'all') return -1;
        if (b.id === 'all') return 1;

        if (accountBarOrder) {
          const order = (accountBarOrder[a.id] ?? Infinity) - (accountBarOrder[b.id] ?? Infinity);
          if (order) {
            return order;
          }
        }

        if (a.displayLabel === 'Personal') return 1;
        if (b.displayLabel === 'Personal') return -1;

        if (a.id === defaultBusiness?.id) return -1;
        if (b.id === defaultBusiness?.id) return 1;

        return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
      });
    }

    const rolledUpNodes = rootNodes.map((node) =>
      rollupAccountNodes({
        node,
        balancesById,
        hideGoals: Boolean(hideGoals),
        ignoreHiddenGrouping,
        investTotals,
        securitiesFF: Boolean(securitiesFF),
      }),
    );

    const accountNodes = rolledUpNodes.map((node) => {
      if (node.id !== 'all' || !node.allAccountIds) {
        return node;
      }
      // Memoized like every other node's ids, so balance and account refreshes keep the same list
      // and its consumers (e.g. the Transactions register) keep their memoized results.
      return node.merge({
        allAccountIds: memoizedAccountIdList(
          node.allAccountIds.filter((accountId) => accountsById.get(accountId)?.type !== 'INVESTMENT').toArray(),
        ),
      });
    });

    // Account refreshes rebuild every node; reuse the value-equal ones so untouched rows don't re-render.
    const paramsKey = `${ignoreUsageType}-${includeClosedOverride}-${ignoreHiddenGrouping}`;
    const previousTree = previousTreesByParams.get(paramsKey);
    const tree = previousTree ? reuseAccountNodes(previousTree, List(accountNodes)) : List(accountNodes);
    previousTreesByParams.set(paramsKey, tree);
    return tree;
  },
);

const getAccountsByIdForNode = (node: AccountNode): Array<[string, AccountNode]> => {
  const flattenedList: Array<[string, AccountNode]> = node.id ? [[node.id, node]] : [];
  if (List.isList(node.children)) {
    return node.children.reduce<Array<[string, AccountNode]>>(
      (list, child) => list.concat(getAccountsByIdForNode(child)),
      flattenedList,
    );
  }
  return flattenedList;
};

export const getAccountNodesById = createSelector([getAccountNodesTree], (tree): OrderedMap<string, AccountNode> => {
  if (!tree || tree.size === 0) {
    return OrderedMap();
  }
  return OrderedMap(
    tree.reduce<Array<[string, AccountNode]>>((list, node) => list.concat(getAccountsByIdForNode(node)), []),
  );
});

export const getIsBlockedBusinessAccountNode = createSelector(
  [getAccountNodesById, accountsSelectors.getAccountsById, (_state, nodeId: string) => nodeId],
  (accountNodesById, accountsById, nodeId): boolean => {
    const accountNode = accountNodesById.get(nodeId);
    return (
      accountNode?.allAccountIds?.every((accountId) => {
        const account = accountsById.get(accountId);
        const accountType = account?.type;
        const accountSubType = account?.subType;
        return (
          (accountType != null && blockedAccountTypes.has(accountType)) ||
          (accountSubType != null && blockedAccountSubTypes.has(accountSubType))
        );
      }) ?? false
    );
  },
);

export const getIsQbpAccountNode = createSelector(
  [getAccountNodesById, accountsSelectors.getAccountsById, (_state, nodeId: string) => nodeId],
  (accountNodesById, accountsById, nodeId): boolean => {
    const accountNode = accountNodesById.get(nodeId);
    return (
      accountNode?.allAccountIds?.every((accountId) => {
        const account = accountsById.get(accountId);
        return account ? accountsUtils.isQBPAccount(account) : false;
      }) ?? false
    );
  },
);

const isLegacyAssetOrLiabilityType = (accountType: string | null | undefined): boolean =>
  accountType === 'ASSET' || accountType === 'LIABILITY';

export const getTransactionsTabVisibilityByNodeId = createSelector(
  [getAccountNodesById, accountsSelectors.getAccountsById, (_state, nodeId: string) => nodeId],
  (accountNodesById, accountsById, nodeId): TransactionsTabVisibility => {
    const accountNode = accountNodesById.get(nodeId);
    const accountIds = accountNode?.allAccountIds;
    if (!accountIds || accountIds.size === 0) {
      return {
        showSpendingTab: true,
        showIncomeTab: true,
      };
    }

    const accounts = accountIds
      .map((accountId) => accountsById.get(accountId))
      .filter((account): account is NonGoalAccount => Boolean(account));
    if (accounts.size === 0) {
      return {
        showSpendingTab: true,
        showIncomeTab: true,
      };
    }

    const isOnlyCreditCardAccounts = accounts.every(
      (account) => account.type === 'CREDIT' && account.subType === 'CREDIT_CARD',
    );
    const isOnlyAssetOrLiabilityAccounts = accounts.every(
      (account) =>
        isLegacyAssetOrLiabilityType(account.type) ||
        (classifyAccountType(account.type) === 'LIABILITY' && account.type !== 'CREDIT'),
    );

    return {
      showSpendingTab: !isOnlyAssetOrLiabilityAccounts,
      showIncomeTab: !isOnlyCreditCardAccounts && !isOnlyAssetOrLiabilityAccounts,
    };
  },
  // Account refreshes recompute this; keep the reference while both flags are unchanged.
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

export const getAccountNodeById = createSelector(
  [getAccountNodesById, (_state, id: string) => id],
  (accountNodes, id): AccountNode | undefined => accountNodes.get(id),
);

const emptyBalanceLabelModeOptions: BalanceLabelModeOptions = {};
const getBalanceLabelModeOptions = (
  _state: State,
  _accountNodeId: string,
  options?: BalanceLabelModeOptions,
): BalanceLabelModeOptions => options || emptyBalanceLabelModeOptions;
const getManualAccountBalanceRefreshEnabled = (state: State): boolean =>
  Boolean(featureFlagsSelectors.getFeatureFlag(state, 'manualAccountBalanceRefresh'));
const getAccountsBankBalancePreference = (state: State): string =>
  preferencesV2Selectors.getPreferenceValue(state, 'sAccountsBankBalance', ShowBalance.INCLUDE_PENDING) ??
  ShowBalance.INCLUDE_PENDING;

const hasValidAsOfDate = (isoDate?: string | null): boolean => Boolean(isoDate && DateTime.fromISO(isoDate).isValid);

const isAppleCashAccount = (account: Account | undefined): boolean => {
  const aggregator = List.isList(account?.aggregators) ? account?.aggregators?.first?.() : undefined;
  const isAppleWallet = aggregator?.channel === APPLE_WALLET_CHANNEL_TYPE;
  return Boolean(isAppleWallet && account?.type === 'BANK' && account?.subType === 'DIGITAL_CASH');
};

export const getBalanceLabelModeForAccountNode = createSelector(
  [
    getAccountNodesById,
    accountsSelectors.getAccountsById,
    institutionLoginsSelectors.getInstitutionLoginsById,
    getManualAccountBalanceRefreshEnabled,
    getAccountsBankBalancePreference,
    (_state, accountNodeId: string) => accountNodeId,
    getBalanceLabelModeOptions,
  ],
  (
    accountNodesById,
    accountsById,
    institutionLoginsById,
    manualAccountBalanceRefreshEnabled,
    accountsBankBalancePreference,
    accountNodeId,
    options,
  ): BalanceLabelMode => {
    const accountNode = accountNodesById.get(accountNodeId);
    const account = accountsById.get(accountNodeId);
    const institutionLogin = account?.institutionLoginId
      ? institutionLoginsById.get(account.institutionLoginId)
      : undefined;

    const includePending =
      typeof options.includePendingOverride === 'boolean'
        ? options.includePendingOverride
        : accountsBankBalancePreference !== ShowBalance.BANK_REPORTED;

    const isManualRefreshEnabled =
      typeof options.manualRefreshEnabledOverride === 'boolean'
        ? options.manualRefreshEnabledOverride
        : manualAccountBalanceRefreshEnabled;

    const isAppleTotalBalance =
      typeof options.isAppleTotalBalanceOverride === 'boolean'
        ? options.isAppleTotalBalanceOverride
        : isAppleCashAccount(account);

    const isLeaf = Boolean(accountNode && !accountNode.children);
    const isAccountOpen = account?.isClosed !== true;
    const isManualAccount = account?.isConnected !== true;
    const isAssetAccount = account ? assetAccountTypes.has(account.type) : false;
    const isZillowOrKbb = institutionLogin?.channel === 'ZILLOW' || institutionLogin?.channel === 'KELLEY_BLUE_BOOK';

    return accountsUtils.getBalanceLabelMode({
      hasAccountNode: Boolean(accountNode),
      isLeaf,
      hasAccount: Boolean(account),
      isAccountOpen,
      isManualRefreshEnabled,
      isManualAccount,
      isAssetAccount,
      isZillowOrKbb,
      isAppleCash: isAppleTotalBalance,
      isConnected: account?.isConnected === true,
      isBankAccount: account?.type === 'BANK',
      isCreditAccount: account?.type === 'CREDIT',
      isLoanAccount: account?.type === 'LOAN',
      includePending,
      hasBalanceAsOfLabel: hasValidAsOfDate(account?.currentBalanceAsOfOn),
    });
  },
);

const accountIdToDisplayLabel: Record<L2GroupingId, string> = {
  BANKING: 'Banking',
  LIABILITY: 'Liabilities',
  ASSET: 'Assets',
  INVESTMENT: 'Investments',
};

export const getAccountsToShowAddOptionUnder = createSelector([getAccountNodesById], (accountNodesById): string[] => {
  return l2Substrings
    .filter((accountOption) => !accountNodesById.some((accountNode) => (accountNode.id ?? '').includes(accountOption)))
    .map((id) => accountIdToDisplayLabel[id]);
});

export const getAccountReorderNodes = createSelector(
  [getAccountNodesTree],
  (accountNodesTree): AccountReorderNode[] => {
    const topLevelNodes = accountNodesTree.slice(1);

    const hasl0 = topLevelNodes.find((node) => node.depth === 1 && node.displayOrder === 0);
    if (!hasl0) {
      return [
        {
          businessId: 'PERSONAL',
          displayThemeColor: 'background.layer1',
          displayThemeBgColor: 'text.primary',
          displayLabel: 'Personal',
        },
      ];
    }

    return topLevelNodes.toArray().map((l0Node) => ({
      businessId: l0Node.id ?? '',
      displayThemeColor: l0Node.displayThemeColor ?? 'text.primary',
      displayThemeBgColor: l0Node.displayThemeBgColor ?? 'background.layer1',
      displayLabel: l0Node.displayLabel ?? '',
    }));
  },
);

export const getTransactionsStatusBannerState = createSelector(
  [
    getAccountNodeById,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'fiStatus'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'hideConnectedLoanTransactions'),
    accountsSelectors.getAccountsById,
    institutionLoginsSelectors.getInstitutionLoginsById,
    (state) => state,
  ],
  (
    accountNode,
    fiStatusEnabled,
    hideConnectedLoanTransactions,
    accountsById,
    institutionLoginsById,
    state,
  ): TransactionsStatusBannerState => {
    const account = accountNode?.account ?? undefined;
    const institutionLogin = account?.institutionLoginId
      ? institutionLoginsById?.get(account.institutionLoginId)
      : undefined;

    const isLeaf = accountNode && !accountNode.children;
    const isAccountOpen = accountNode?.account?.isClosed !== true;

    const hasBlockedLoanAccount = (): boolean => {
      const firstAccountId = accountNode?.allAccountIds?.first?.();
      if (firstAccountId) {
        const firstAccount = accountsById.get(firstAccountId);
        return (
          firstAccount?.type === 'LOAN' &&
          accountsUtils.transactionsNotSupportedForAccountId(
            accountNode?.id ?? '',
            Boolean(hideConnectedLoanTransactions),
            state,
          )
        );
      }
      return false;
    };

    const isLoanPage = accountNode ? Boolean(isLeaf && hasBlockedLoanAccount()) : false;
    const showAppleWalletBanner = institutionLogin?.channel === APPLE_WALLET_CHANNEL_TYPE;
    const accountAggStatus = account?.aggregators?.first()?.aggStatus as AggStatus | null | undefined;
    const isAccountAggError =
      institutionLogin && accountAggStatus && institutionLoginsUtils.isErrorType(accountAggStatus);
    const institutionFiIssue = institutionLogin?.id
      ? institutionLoginsSelectors.getFiIssueForLogin(state, institutionLogin.id, false)
      : undefined;
    const isAccountFiInfoIssue = institutionFiIssue?.issueType === 'INFO';
    const showInstitutionStatusBanner = Boolean(
      fiStatusEnabled &&
        accountNode &&
        !isLoanPage &&
        isAccountOpen &&
        institutionLogin?.id &&
        (isAccountAggError || isAccountFiInfoIssue),
    );

    return {
      showAppleWalletBanner,
      showInstitutionStatusBanner,
      institutionLoginId: institutionLogin?.id ?? null,
      account,
    };
  },
  // The `state` input recomputes on every action; keep the reference stable when the fields are unchanged.
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
);

export const getStatusBannerVisibilityForAccountId = createSelector(
  [
    (state: State, accountId: string | undefined) => accountsSelectors.getAccountById(state, accountId),
    (state, accountId: string | undefined) => {
      const institutionLoginId = accountId
        ? accountsSelectors.getAccountById(state, accountId)?.institutionLoginId
        : undefined;
      const institutionLogin = institutionLoginId
        ? institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId)
        : undefined;
      return institutionLogin;
    },
  ],
  (account, institutionLogin): StatusBannerVisibilityForAccountId => {
    const aggStatus = account?.aggregators?.first()?.aggStatus as AggStatus | null | undefined;
    const isAppleWallet = institutionLogin?.channel === APPLE_WALLET_CHANNEL_TYPE;
    const isAggError = institutionLogin && aggStatus && institutionLoginsUtils.isErrorType(aggStatus);
    const lastRefreshSuccessfulAt = institutionLogin?.aggregators?.first?.()?.lastRefreshSuccessfulAt;
    const lastSyncAt = lastRefreshSuccessfulAt ? DateTime.fromISO(lastRefreshSuccessfulAt) : null;
    const appleWalletShowWarning = !isAggError && lastSyncAt ? lastSyncAt.diffNow('days').days < -1 : false;
    const appleWalletVisible = Boolean(isAppleWallet && (isAggError || appleWalletShowWarning));
    const isFiInfoIssue = institutionLogin?.fiIssue?.issueType === 'INFO';
    return {
      appleWalletVisible,
      accountVisible:
        aggStatus || isFiInfoIssue
          ? Boolean(institutionLoginsUtils.isErrorType(aggStatus) || isFiInfoIssue)
          : undefined,
    };
  },
);

export const getCanShowCreditInfoForAccountNode = createSelector(
  [getTransactionsStatusBannerState, getStatusBannerVisibilityForAccountId],
  (statusState, accountStatusBannerVisibility): boolean => {
    const appleWalletVisible = Boolean(
      statusState.showAppleWalletBanner && accountStatusBannerVisibility?.appleWalletVisible,
    );
    const accountVisible = Boolean(
      statusState.showInstitutionStatusBanner && accountStatusBannerVisibility?.accountVisible,
    );

    return !appleWalletVisible && !accountVisible;
  },
);
